<template>
  <v-container fluid class="help-page">
    <!-- Kepala halaman -->
    <div class="help-head">
      <div class="help-head__text">
        <p class="help-eyebrow">
          <v-icon x-small>mdi-help-circle-outline</v-icon>
          Bantuan · Panduan Pengguna
        </p>
        <h2 class="help-title">
          Panduan <mark class="help-title__role">{{ role.label }}</mark>
        </h2>
        <p class="help-sub">
          Langkah-langkah menggunakan e-Sign Universitas Indonesia sesuai peran Anda.
          Diperbarui {{ doc.diperbarui }}.
        </p>
      </div>
      <div class="help-head__actions">
        <v-text-field
          v-model="query"
          class="help-search"
          prepend-inner-icon="mdi-magnify"
          placeholder="Cari di panduan…"
          outlined
          dense
          clearable
          hide-details
        />
        <v-btn
          outlined
          color="primary"
          class="help-download"
          :href="pdfUrl"
          :download="pdfName"
          target="_blank"
          rel="noopener"
        >
          <v-icon left small>mdi-file-pdf-box</v-icon>
          Unduh PDF
        </v-btn>
      </div>
    </div>

    <div v-if="roles.length > 1" class="help-roles" role="tablist" aria-label="Pilih panduan">
      <span class="help-roles__label">Lihat panduan:</span>
      <button
        v-for="r in roles"
        :key="r.kode"
        type="button"
        role="tab"
        :aria-selected="r.kode === activeKode"
        class="help-roles__chip"
        :class="{ 'help-roles__chip--active': r.kode === activeKode }"
        @click="selectRole(r.kode)"
      >
        {{ r.label }}
      </button>
    </div>

    <div class="help-body">
      <!-- Daftar isi -->
      <nav class="help-toc" aria-label="Daftar isi">
        <p class="help-toc__title">Daftar Isi</p>
        <ol class="help-toc__list">
          <li v-for="(s, i) in visibleSections" :key="s.id">
            <a
              :href="'#' + anchor(s.id)"
              :class="{ 'is-active': activeId === s.id }"
              @click.prevent="scrollTo(s.id)"
            >{{ i + 1 }}. {{ s.title }}</a>
            <ol v-if="subheads(s).length" class="help-toc__sub">
              <li v-for="h in subheads(s)" :key="h.id">
                <a
                  :href="'#' + anchor(h.id)"
                  :class="{ 'is-active': activeId === h.id }"
                  @click.prevent="scrollTo(h.id)"
                >{{ h.text }}</a>
              </li>
            </ol>
          </li>
          <li v-if="!query">
            <a :href="'#' + anchor('faq')" @click.prevent="scrollTo('faq')">FAQ</a>
          </li>
        </ol>
      </nav>

      <!-- Isi -->
      <article ref="content" class="help-content">
        <div v-if="!query" class="help-intro">
          <help-block v-for="(b, i) in role.intro" :key="'intro-' + i" :block="b" @zoom="zoom" />
        </div>

        <p v-if="query && !visibleSections.length" class="help-empty">
          Tidak ada bagian yang memuat “{{ query }}”.
        </p>
        <p v-else-if="query" class="help-empty">
          {{ visibleSections.length }} bagian memuat “{{ query }}”.
        </p>

        <section
          v-for="(s, i) in visibleSections"
          :id="anchor(s.id)"
          :key="role.kode + s.id"
          :data-spy="s.id"
          class="help-section"
        >
          <h3 class="help-section__title">{{ i + 1 }}. {{ s.title }}</h3>
          <help-block
            v-for="(b, j) in s.blocks"
            :key="s.id + '-' + j"
            :block="b"
            :anchor-id="b.h3 ? anchor(subId(s, b)) : null"
            :spy-id="b.h3 ? subId(s, b) : null"
            @zoom="zoom"
          />
        </section>

        <section v-if="!query" :id="anchor('faq')" data-spy="faq" class="help-section">
          <h3 class="help-section__title">FAQ</h3>
          <div v-for="(f, i) in faq" :key="'faq-' + i" class="help-faq">
            <p class="help-faq__q">{{ i + 1 }}. {{ f[0] }}</p>
            <!-- eslint-disable-next-line vue/no-v-html -->
            <p class="help-faq__a" v-html="fmt(f[1])"></p>
          </div>
        </section>
      </article>
    </div>

    <v-dialog v-model="zoomOpen" max-width="1200" content-class="help-zoom">
      <v-card v-if="zoomImg">
        <img :src="zoomImg.src" :alt="zoomImg.caption" class="help-zoom__img">
        <v-card-actions>
          <span class="help-zoom__cap">{{ zoomImg.caption }}</span>
          <v-spacer />
          <v-btn text color="primary" @click="zoomOpen = false">Tutup</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import content from '@/help/content'
import { fmt, slugify, blockText } from '@/help/format'
import HelpBlock from './HelpBlock.vue'

const IMG_BASE = '/help/img/'

export default {
  name: 'PanduanPengguna',
  components: { HelpBlock },
  data () {
    return {
      doc: content.DOC,
      faq: content.FAQ,
      activeKode: null,
      activeId: null,
      query: '',
      zoomOpen: false,
      zoomImg: null,
      observer: null,
    }
  },
  computed: {
    // Administrator melihat semua panduan; peran lain hanya panduan perannya sendiri.
    roles () {
      const user = JSON.parse(localStorage.getItem('user') || 'null')
      const owned = ((user && user.list_peran) || []).map(p => p.kode_peran)
      if (owned.includes('A01')) return content.ROLES
      const mine = content.ROLES.filter(r => owned.includes(r.kode))
      return mine.length ? mine : content.ROLES.filter(r => r.kode === 'A02')
    },
    role () {
      return this.roles.find(r => r.kode === this.activeKode) || this.roles[0]
    },
    pdfName () {
      return `User_Manual_Esign_${this.role.slug}.pdf`
    },
    pdfUrl () {
      return `/help/${this.pdfName}`
    },
    visibleSections () {
      const q = (this.query || '').trim().toLowerCase()
      if (!q) return this.role.sections
      return this.role.sections.filter(s =>
        (s.title + ' ' + s.blocks.map(blockText).join(' ')).toLowerCase().includes(q))
    },
  },
  watch: {
    activeKode () { this.$nextTick(this.observe) },
    query () { this.$nextTick(this.observe) },
  },
  created () {
    const fromQuery = this.$route.query.peran
    const current = localStorage.getItem('currentPeran')
    const pick = [fromQuery, current].find(k => this.roles.some(r => r.kode === k))
    this.activeKode = pick || this.roles[0].kode
  },
  mounted () {
    this.observe()
  },
  beforeDestroy () {
    if (this.observer) this.observer.disconnect()
  },
  methods: {
    fmt,
    anchor (id) { return `${this.role.kode.toLowerCase()}-${id}` },
    subId (section, block) { return `${section.id}--${slugify(block.h3)}` },
    subheads (section) {
      return section.blocks.filter(b => b.h3).map(b => ({ id: this.subId(section, b), text: b.h3 }))
    },
    selectRole (kode) {
      this.activeKode = kode
      this.query = ''
      this.$router.replace({ query: { peran: kode } }).catch(() => {})
      window.scrollTo({ top: 0 })
    },
    scrollTo (id) {
      const el = document.getElementById(this.anchor(id))
      if (!el) return
      const top = el.getBoundingClientRect().top + window.pageYOffset - 88
      window.scrollTo({ top, behavior: 'smooth' })
      this.activeId = id
    },
    zoom (block) {
      this.zoomImg = { src: IMG_BASE + block.img, caption: block.caption }
      this.zoomOpen = true
    },
    // Menandai entri daftar isi sesuai bagian yang sedang dibaca.
    observe () {
      if (this.observer) this.observer.disconnect()
      if (!('IntersectionObserver' in window) || !this.$refs.content) return
      this.observer = new IntersectionObserver(entries => {
        const visible = entries.filter(e => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        if (visible.length) this.activeId = visible[0].target.dataset.spy
      }, { rootMargin: '-90px 0px -65% 0px' })
      this.$refs.content.querySelectorAll('[data-spy]').forEach(el => this.observer.observe(el))
    },
  },
}
</script>

<style scoped>
.help-page { padding: 0; }

.help-head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
}
.help-eyebrow {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .12em;
  text-transform: uppercase;
  color: var(--esign-text-subtle);
}
.help-title {
  margin: 0;
  font-size: 22px;
  font-weight: 800;
  color: var(--esign-text);
}
.help-title__role {
  padding: 0 6px;
  border-radius: 4px;
  background: var(--esign-accent);
  color: var(--esign-navy-deep, #0b1f4d);
}
.help-sub {
  margin: 4px 0 0;
  font-size: 13px;
  color: var(--esign-text-muted);
}
.help-head__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.help-search { width: 260px; max-width: 100%; background: var(--esign-surface); }
.help-download { text-transform: none; letter-spacing: 0; font-weight: 600; }

.help-roles {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}
.help-roles__label { font-size: 12px; color: var(--esign-text-muted); }
.help-roles__chip {
  padding: 5px 14px;
  border: 1px solid var(--esign-border);
  border-radius: 999px;
  background: var(--esign-surface);
  font-size: 13px;
  font-weight: 600;
  color: var(--esign-text);
  cursor: pointer;
}
.help-roles__chip:hover { border-color: var(--esign-primary); }
.help-roles__chip--active {
  border-color: var(--esign-navy);
  background: var(--esign-navy);
  color: #fff;
}

.help-body {
  display: grid;
  grid-template-columns: 240px minmax(0, 1fr);
  gap: 24px;
  align-items: start;
}

.help-toc {
  position: sticky;
  top: 88px;
  max-height: calc(100vh - 110px);
  overflow-y: auto;
  padding: 4px 4px 4px 0;
}
.help-toc__title {
  margin: 0 0 8px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .12em;
  text-transform: uppercase;
  color: var(--esign-text-subtle);
}
.help-toc__list, .help-toc__sub { list-style: none; margin: 0; padding: 0; }
.help-toc__sub { padding-left: 12px; }
.help-toc a {
  display: block;
  padding: 4px 8px;
  border-left: 2px solid transparent;
  font-size: 13px;
  line-height: 1.4;
  color: var(--esign-text-muted);
  text-decoration: none;
}
.help-toc__sub a { font-size: 12px; color: var(--esign-text-subtle); }
.help-toc a:hover { color: var(--esign-primary); }
.help-toc a.is-active {
  border-left-color: var(--esign-primary);
  color: var(--esign-primary);
  font-weight: 600;
}

.help-content {
  min-width: 0;
  padding: 24px 28px;
  border: 1px solid var(--esign-border);
  border-radius: 12px;
  background: var(--esign-surface);
}
.help-intro { padding-bottom: 8px; border-bottom: 1px solid var(--esign-border); }
.help-section { padding-top: 24px; scroll-margin-top: 88px; }
.help-section__title {
  margin: 0 0 12px;
  padding-left: 10px;
  border-left: 4px solid var(--esign-accent);
  font-size: 18px;
  font-weight: 700;
  color: var(--esign-text);
}
.help-empty { margin: 8px 0 0; font-size: 13px; color: var(--esign-text-muted); }

.help-faq { margin-bottom: 12px; }
.help-faq__q { margin: 0 0 2px; font-weight: 700; color: var(--esign-text); }
.help-faq__a { margin: 0; color: var(--esign-text-muted); }

.help-zoom__img { display: block; width: 100%; height: auto; }
.help-zoom__cap { font-size: 13px; color: var(--esign-text-muted); }

@media (max-width: 960px) {
  .help-body { grid-template-columns: 1fr; }
  .help-toc {
    position: static;
    max-height: none;
    padding: 12px 16px;
    border: 1px solid var(--esign-border);
    border-radius: 12px;
    background: var(--esign-surface);
  }
  .help-content { padding: 16px; }
}
</style>
